import * as THREE from 'three';
import Engine from './Engine';
import Time from './Time';
import Input from './Input';
import WorkspaceManager from "./WorkspaceManager";
import MathUtils from './utilities/MathUtils';
import WorkspaceUtils from './utilities/WorkspaceUtils';
import ScopeCamera from './Camera';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import AlignToViewAction from '../actions/AlignToViewAction';
import { CameraProjectionModes, SimulationStates, ValueTypes, ZoomBehaviors} from '../globals';
import { ModifierKeyCodes, MouseButtons } from '../interfaces/Input';
import store from '../state/store';

const mouseDollyReferenceVector = new THREE.Vector2(1, 1);
const defaultFocusDistance = 0.3;
const cameraForward = new THREE.Vector3();
const cameraRight = new THREE.Vector3();
const tempBox = new THREE.Box3();
const fullscreenRect = { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };

export type SceneCameraControllerOptions = {
	panSensitivity?: number;
	orbitSensitivity?: number;
	mouseDollySensitivity?: number;
	wheelDollySensitivity?: number;
	minDollyDistance?: number;
	maxDollyDistance?: number;
	cameraFlightSpeed?: number;
	cameraSpeedBoostFactor?: number;
	animationDuration?: number;
	animationThreshold?: number;
};

export default class SceneCameraController
{
	static homePosition = new THREE.Vector3(0.25, 0.25, 0.5);

	private panSensitivity: number;
	private orbitSensitivity: number;
	private mouseDollySensitivity: number;
	private wheelDollySensitivity: number;
	private maxDollyDistance: number;
	private minDollyDistance: number;
	private cameraFlightSpeed: number;
	private cameraSpeedBoostFactor: number;
	private animationDuration: number;
	private animationTime: number;
	private animationThreshold: number;
	private lastMousePosition: THREE.Vector2;
	private lastTouchPosition: THREE.Vector2;
	private wasTouching: boolean;
	private mouseDelta: THREE.Vector2;
	private touchDelta: THREE.Vector2;
	private _anchor: THREE.Vector3;
	private _animationTargetPosition: THREE.Vector3;
	private animationTargetQuaternion: THREE.Quaternion;
	private _busy: boolean;
	private _camera: THREE.Camera;
	private _animateTransform: boolean;
	private animateProjection: boolean;
	private keepLookAtDuringAnimation: boolean;
	private currentProjectionMatrix: THREE.Matrix4;
	private targetProjectionMatrix: THREE.Matrix4;
	private hoveredElement: HTMLElement | null = null;

	private get isSimulatingAsViewer(): boolean
	{
		return store.getState().applicationModule.simulationState === SimulationStates.Playing && store.getState().applicationModule.simulationAsViewer;
	}

	get animationTargetPosition(): THREE.Vector3
	{
		return this._animationTargetPosition;
	}
	get animateTransform(): boolean
	{
		return this._animateTransform;
	}

	set animateTransform(value: boolean)
	{
		this._animateTransform = value;
	}

	constructor(camera: THREE.Camera, options: SceneCameraControllerOptions = {})
	{
		const defaults = { ...options,
			panSensitivity: 1,
			orbitSensitivity: 2.0,
			mouseDollySensitivity: 50,
			wheelDollySensitivity: 2.5,
			minDollyDistance: (camera as THREE.OrthographicCamera).near * 2,
			maxDollyDistance: (camera as THREE.OrthographicCamera).far,
			cameraFlightSpeed: 1,
			cameraSpeedBoostFactor: 3,
			animationDuration: 0.5,
			animationThreshold: 0.01
		}

		this.panSensitivity = defaults.panSensitivity;
		this.orbitSensitivity = defaults.orbitSensitivity
		this.mouseDollySensitivity = defaults.mouseDollySensitivity;
		this.wheelDollySensitivity = defaults.wheelDollySensitivity;
		this.minDollyDistance = defaults.minDollyDistance;
		this.maxDollyDistance = defaults.maxDollyDistance;
		this.cameraFlightSpeed = defaults.cameraFlightSpeed;
		this.cameraSpeedBoostFactor = defaults.cameraSpeedBoostFactor;
		this.animationDuration = defaults.animationDuration;
		this.animationThreshold = defaults.animationThreshold;

		this.animationTime = 0;
		this.lastMousePosition = new THREE.Vector2();
		this.lastTouchPosition = new THREE.Vector2();
		this.wasTouching = false;
		this.mouseDelta = new THREE.Vector2();
		this.touchDelta = new THREE.Vector2();
		this._anchor = new THREE.Vector3();
		this._animationTargetPosition = new THREE.Vector3();
		this.animationTargetQuaternion = new THREE.Quaternion();
		this._busy = false;
		this.keepLookAtDuringAnimation = false;
		this.currentProjectionMatrix = new THREE.Matrix4();
		this.targetProjectionMatrix = new THREE.Matrix4();

		this._camera = camera;
		this._animateTransform = false;
		this.animateProjection = false;
		this._anchor.set(0, 0, 0);
		this.mouseDelta.set(0, 0);
		this.touchDelta.set(0, 0);

		document.addEventListener("mousemove", this.onMouseMove);
	}

	onMouseMove = (event: MouseEvent) =>
	{
		this.hoveredElement = event.target as HTMLElement;
	}

	resetMouseDelta()
	{
		this.mouseDelta.set(0, 0);
		this.touchDelta.set(0, 0);
		this.lastMousePosition = Input.getMousePositionInRect(fullscreenRect);
		this.lastTouchPosition = Input.getTouchPositionInRect(fullscreenRect)?.position ?? new THREE.Vector2(0, 0);
	}

	get camera()
	{
		return this._camera;
	}

	get anchor()
	{
		return this._anchor;
	}

	setCamera(targetCamera, animateProjection: boolean = true)
	{
		if(this._camera)
		{
			targetCamera.position.copy(this._camera.position);
			targetCamera.quaternion.copy(this._camera.quaternion);

			//TODO: this doesn't quite work when going back and forth between modes
			//perspective to ortho is based on distance to anchor, and ortho to perspective forces that distance to change (unless the orthographic size didn't change)
			//that means that going 'perspective->orthographic->change orthograpic size' will produce a certian view, but then going back to perspective and back to orthograpic would produce a somewhat differnet view
			//the root of the issue is that we want to keep our perspective camera's fov a constant 60, which is very limiting for this case
			if(targetCamera.isOrthographicCamera && this._camera instanceof THREE.PerspectiveCamera)
			{
				//https://stackoverflow.com/questions/48187416/how-to-switch-between-perspective-and-orthographic-cameras-keeping-size-of-desir
				const sceneViewData = WorkspaceManager.getSceneViewData(Engine.getInstance().getActiveSceneViewId());
				const renderRect = sceneViewData?.context.canvas.getBoundingClientRect() || { width: window.innerWidth, height: window.innerHeight };
				const aspectRatio = Math.ceil(renderRect.width) / Math.ceil(renderRect.height);

				const distance = this.getDistanceToAnchor();
				const depth = Math.tan((this._camera.fov / 2.0) * Math.PI / 180.0 ) * 2.0;
				const halfHeight = depth * distance  / 2;
				const halfWidth = halfHeight * aspectRatio;
				targetCamera.left = -halfWidth;
				targetCamera.right =  halfWidth;
				targetCamera.top = halfHeight;
				targetCamera.bottom = -halfHeight;
			}
			else if((targetCamera as THREE.PerspectiveCamera).isPerspectiveCamera && this._camera instanceof THREE.OrthographicCamera)
			{
				const height = (this._camera.top - this._camera.bottom);
				const depth =  Math.tan((targetCamera.fov / 2.0) * Math.PI / 180.0 ) * 2.0;
				const desiredDistance = height / depth;
				targetCamera.position.clone(this._anchor).add(new THREE.Vector3().subVectors(this._camera.position, this._anchor).normalize().multiplyScalar(desiredDistance));
			}
		}
		targetCamera.updateProjectionMatrix();
		if (animateProjection)
		{
			this.setProjectionAnimation(this._camera.projectionMatrix, targetCamera.projectionMatrix);
		}
		this._camera = targetCamera;

		Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
	}

	get busy()
	{
		return this._busy;
	}

	set busy(value: boolean)
	{
		this._busy = value;
	}

	update()
	{
		const currentMousePosition = Input.getMousePositionInRect(fullscreenRect);
		const currentTouchPositionObj = Input.getTouchPositionInRect(fullscreenRect);
		const isTouching = !!currentTouchPositionObj;
		const currentTouchPosition = currentTouchPositionObj?.position ?? this.lastTouchPosition.clone();
		this.mouseDelta.subVectors(currentMousePosition, this.lastMousePosition);
		this.lastMousePosition = currentMousePosition;
		if (currentTouchPositionObj?.firstTouch || (isTouching && !this.wasTouching))
		{
			this.lastTouchPosition = currentTouchPosition.clone();
		}
		this.touchDelta.subVectors(currentTouchPosition, this.lastTouchPosition);
		this.lastTouchPosition = currentTouchPosition.clone();
		this.wasTouching = isTouching;

		if (!WorkspaceUtils.shouldAllowNativeBehavior())
		{
			if (Input.getKeybindDown(Input.KeyBindings.AlignToView))
			{
				this.alignToView();
			}
			else if (Input.getKeybindDown(Input.KeyBindings.CameraFocus))
			{
				this.focus();
			}
			else if (Input.getKeybindDown(Input.KeyBindings.CameraRestoreOrientation))
			{
				this.goHome();
			}
			else if (Input.getKeybindDown(Input.KeyBindings.CameraProjectionMode))
			{
				Engine.getInstance().getActiveSceneView()?.setCameraProjectionMode(
					(this._camera instanceof THREE.PerspectiveCamera) ? CameraProjectionModes.Orthographic : CameraProjectionModes.Perspective);
			}
		}

		// camera animation comes first and overrides normal movement
		if (this._animateTransform || this.animateProjection)
		{
			this.animate();
			return;
		}

		if(this.isSimulatingAsViewer)
		{
			if (Input.getMouseButton(MouseButtons.LEFT) || Input.getTouchCount() === 1)
			{
				this.orbit();
			}
			else if (Input.getMouseButton(MouseButtons.RIGHT))
			{
				this.dollyToMouse();
			}
			else if (Input.getMouseButton(MouseButtons.MIDDLE))
			{
				this.pan();
			}
			else if (Input.getTouchCount() === 2)
			{
				const zoomSensitivity = 25;
				if (Input.touchZoom)
				{
					this.dolly(Input.touchZoom * -zoomSensitivity * Time.getDeltaTime() * Math.min(this.getDistanceToAnchor(), zoomSensitivity * 10), ZoomBehaviors.CameraForward);
				}
				this.pan();
			}
			const wheelDelta = Input.getMouseWheelDelta();
			if (wheelDelta !== 0)
			{
				this.dolly(wheelDelta * Time.getDeltaTime() * this.wheelDollySensitivity * Math.min(this.getDistanceToAnchor(), this.wheelDollySensitivity * 10), ZoomBehaviors.TowardsCursor);
			}
			if (!MathUtils.areVectorsEqual(this._anchor, this._camera.position)) this._camera.lookAt(this._anchor);
			return;
		}

		// orbit navigation (maya-like)
		if (!Engine.getInstance().getPickingManager().isBusy())
		{
			this._busy = false;
			if (Input.getModifier(ModifierKeyCodes.ALT))
			{
				this._busy = true;
				if (Input.getMouseButton(MouseButtons.LEFT))
				{
					this.orbit();
				}

				if (Input.getMouseButton(MouseButtons.RIGHT))
				{
					this.dollyToMouse()
				}

				if (Input.getMouseButton(MouseButtons.MIDDLE))
				{
					this.pan();
				}
			}
			// if not orbit, we check for keyboard-panning (especialy useful for laptops or mouse pads without middle-mouse buttons that can't pan otherwise)
			else if (Input.getModifier(ModifierKeyCodes.CTRL))
			{
				if (Input.getMouseButton(MouseButtons.RIGHT))
				{
					this._busy = true;
					this.pan();
				}
			}
			// if not orbit, we need to check for flight
			else if (Input.getMouseButton(MouseButtons.RIGHT))
			{
				this._busy = true;
				this.fly();
			}
			// otherwise, we check for panning
			else if (Input.getMouseButton(MouseButtons.MIDDLE))
			{
				this._busy = true;
				this.pan();
			}
		}

		// scroll wheel also dollies (regardless of mode or selection state)
		const wheelDelta = Input.getMouseWheelDelta();
		if (wheelDelta !== 0)
		{
			if(this.hoveredElement?.getAttribute("data-viewport"))
			{
				this.dolly(wheelDelta * Time.getDeltaTime() * this.wheelDollySensitivity * Math.min(this.getDistanceToAnchor(), this.wheelDollySensitivity * 10), ZoomBehaviors.TowardsCursor);
			}
		}

		// finish the frame by always looking at the anchor
		if (!MathUtils.areVectorsEqual(this._anchor, this._camera.position)) this._camera.lookAt(this._anchor);
	}

	getDistanceToAnchor()
	{
		return new THREE.Vector3().subVectors(this._anchor, this._camera.position).length();
	}

	dollyToMouse()
	{
		const delta = new THREE.Vector2().addVectors(this.mouseDelta, mouseDollyReferenceVector.clone().multiplyScalar(Input.touchZoom ?? 0));
		const dollyDirection = mouseDollyReferenceVector.dot(delta) > 0 ? -delta.length() : delta.length();
		this.dolly(dollyDirection * Time.getDeltaTime() * this.getDistanceToAnchor() * this.mouseDollySensitivity, ZoomBehaviors.CameraForward);
	}

	dolly(amount: number, zoomBehavior: ZoomBehaviors)
	{
		if (!amount) return;
		if (store.getState().preferencesModule.editorPreferences.invertZoom) amount *= -1;

		if (this._camera instanceof THREE.PerspectiveCamera)
		{
			if(zoomBehavior === ZoomBehaviors.TowardsCursor)
			{
				const mousePosition = Input.getMousePosition();
				const projectedMousePosition = new THREE.Vector3(mousePosition.x, mousePosition.y, 0.5);
				projectedMousePosition.unproject(this._camera);
				const dollyVector = new THREE.Vector3().subVectors(projectedMousePosition, this._camera.position).normalize().multiplyScalar(-amount);
				this._anchor.add(dollyVector);
				this._camera.position.add(dollyVector);
			}
			else if(zoomBehavior === ZoomBehaviors.TowardsTouchCenter)
			{
				const touchCenter = Input.getTouchZoomCenter();
				if(!touchCenter) return;
				const projectedTouchCenter = new THREE.Vector3(touchCenter.x, touchCenter.y, 0.5);
				projectedTouchCenter.unproject(this._camera);
				const dollyVector = new THREE.Vector3().subVectors(projectedTouchCenter, this._camera.position).normalize().multiplyScalar(amount);
				this._anchor.add(dollyVector);
				this._camera.position.add(dollyVector);
			}
			else
			{
				this._camera.getWorldDirection(cameraForward);
				this._camera.position.add(cameraForward.multiplyScalar(-amount));
			}
		}
		else if ( this._camera instanceof THREE.OrthographicCamera)
		{
			const sceneViewData = WorkspaceManager.getSceneViewData(Engine.getInstance().getActiveSceneViewId());
			const renderRect = sceneViewData?.context.canvas.getBoundingClientRect() || { width: window.innerWidth, height: window.innerHeight };
			const aspectRatio = Math.ceil(renderRect.width) / Math.ceil(renderRect.height);
			let currentOrthographicSize = (1 / ((aspectRatio * -0.5) / this._camera.left));
			currentOrthographicSize = Math.max(0.001, currentOrthographicSize + amount);
			this._camera.left = currentOrthographicSize * aspectRatio * -0.5;
			this._camera.right = currentOrthographicSize * aspectRatio * 0.5;
			this._camera.top = currentOrthographicSize * 0.5;
			this._camera.bottom = currentOrthographicSize * -0.5;

			if(zoomBehavior === ZoomBehaviors.TowardsCursor)
			{
				//god bless this man https://stackoverflow.com/questions/57638051/how-to-fix-zoom-towards-mouse-routine
				const mousePosition = Input.getMousePosition();
				const beforeZoomProjectedMousePosition = new THREE.Vector3(mousePosition.x, mousePosition.y, 0.0);
				beforeZoomProjectedMousePosition.unproject(this._camera);
				this._camera.updateProjectionMatrix();
				const afterZoomProjectedMousePosition = new THREE.Vector3(mousePosition.x, mousePosition.y, 0.0);
				afterZoomProjectedMousePosition.unproject(this._camera);
				const projectedDelta = new THREE.Vector3().subVectors(afterZoomProjectedMousePosition, beforeZoomProjectedMousePosition);
				this._camera.position.sub(projectedDelta);
				this._anchor.sub(projectedDelta);
			}
			else
			{
				this._camera.updateProjectionMatrix();
			}
		}
		Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
	}

	fly()
	{
		this._camera.getWorldDirection(cameraForward);
		cameraRight.crossVectors(this._camera.up, cameraForward);

		if (this.mouseDelta.length())
		{
			if (this._camera instanceof THREE.PerspectiveCamera)
			{
				// phi = latitude, north-south (y axis), theta = longitude, east-west (x axis), radius = size (distance)
				const quat = new THREE.Quaternion().setFromUnitVectors(this._camera.up, new THREE.Vector3(0, 1, 0));
				const offset = new THREE.Vector3().subVectors(this._anchor, this._camera.position).applyQuaternion(quat);
				const sphericalMovement = new THREE.Spherical().setFromVector3(offset);

				sphericalMovement.theta -= this.mouseDelta.x * this.orbitSensitivity;
				sphericalMovement.phi -= this.mouseDelta.y * this.orbitSensitivity;
				sphericalMovement.makeSafe();

				offset.setFromSpherical(sphericalMovement).applyQuaternion(quat.invert()).add(this._camera.position);
				this._anchor.copy(offset);
			}
			else
			{
				this.orbit();
			}
		}

		// wasdqe movement - w forward, s backward, a left, d right, q up, e down
		const movement = new THREE.Vector3();
		if (Input.getKeybind(Input.KeyBindings.CameraForward)) 		movement.add(cameraForward);
		if (Input.getKeybind(Input.KeyBindings.CameraBackwards))	movement.sub(cameraForward);
		if (Input.getKeybind(Input.KeyBindings.CameraRight)) 		movement.sub(cameraRight);
		if (Input.getKeybind(Input.KeyBindings.CameraLeft)) 		movement.add(cameraRight);
		if (Input.getKeybind(Input.KeyBindings.CameraUp)) 			movement.add(this._camera.up);
		if (Input.getKeybind(Input.KeyBindings.CameraDown)) 		movement.sub(this._camera.up);

		movement.normalize().multiplyScalar(this.cameraFlightSpeed * Time.getDeltaTime());
		if (Input.getModifier(ModifierKeyCodes.SHIFT))
		{
			movement.multiplyScalar(this.cameraSpeedBoostFactor);
		}

		this._anchor.add(movement);
		this._camera.position.add(movement);
		if (this.mouseDelta.length() || movement.length())
		{
			Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
		}
	}

	orbit()
	{
		const fullDelta = new THREE.Vector2().addVectors(this.mouseDelta, this.touchDelta);
		if (fullDelta.length())
		{
			// phi = latitude, north-south (y axis), theta = longitude, east-west (x axis), radius = size (distance)
			const quat = new THREE.Quaternion().setFromUnitVectors(this._camera.up, new THREE.Vector3(0, -1, 0));
			const offset = new THREE.Vector3().subVectors(this._camera.position, this._anchor).applyQuaternion(quat);
			const sphericalMovement = new THREE.Spherical().setFromVector3(offset);

			sphericalMovement.theta += fullDelta.x * this.orbitSensitivity;
			sphericalMovement.phi -= fullDelta.y * this.orbitSensitivity;
			sphericalMovement.makeSafe();

			offset.setFromSpherical(sphericalMovement).applyQuaternion(quat.invert()).add(this._anchor);
			this._camera.position.set(offset.x, offset.y, offset.z);

			Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
		}
	}

	pan()
	{
		const fullDelta = new THREE.Vector2().addVectors(this.mouseDelta, Input.touchPan?.clone().multiply({x: 1, y: -1}).multiplyScalar(.25) ?? new THREE.Vector2());
		if (fullDelta.length())
		{
			const positionBefore = new THREE.Vector3().copy(this._camera.position);
			const distanceToAnchor = this.getDistanceToAnchor();
			this._camera.translateX(-fullDelta.x * distanceToAnchor * this.panSensitivity);
			this._camera.translateY(-fullDelta.y * distanceToAnchor * this.panSensitivity);

			const delta = new THREE.Vector3().subVectors(this._camera.position, positionBefore);
			this._anchor.add(delta);
			Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
		}
	}

	static focusOnObjects(camera: THREE.Camera, objects: THREE.Object3D[])
	{
		let distance = 0;
		camera.getWorldDirection(cameraForward);
		const anchor = new THREE.Vector3();
		let orthographic: {
			left: number;
			right: number;
			top: number;
			bottom: number;
		} | null = null;

		if (!Array.isArray(objects)) objects = [objects];

		// if nothing is selected, focus the center of the world?
		if (objects.length === 0)
		{
			distance = defaultFocusDistance;
			anchor.set(0, 0, 0);
		}
		// if we do have objects selected, we focus the center of the total boundary
		else
		{
			const selectionBox = new THREE.Box3();
			for (const object of objects)
			{
				const geometry = (object as THREE.Mesh).geometry;
				if (geometry && !geometry.boundingBox) geometry.computeBoundingBox();
				if (geometry && !geometry.boundingBox!.isEmpty())
				{
					tempBox.copy(geometry.boundingBox!);
					object.updateMatrixWorld();
					tempBox.applyMatrix4(object.matrixWorld);
					selectionBox.expandByPoint(tempBox.min);
					selectionBox.expandByPoint(tempBox.max);
				}
				else
				{
					tempBox.makeEmpty();
					SceneObjectUtils.expandBoundsByObjectVisibleOnly(tempBox, object);
					if(!tempBox.isEmpty())
					{
						selectionBox.expandByPoint(tempBox.min);
						selectionBox.expandByPoint(tempBox.max);
					}
					else
					{
						const worldPosition = new THREE.Vector3();
						object.getWorldPosition(worldPosition);
						selectionBox.expandByPoint(worldPosition);
					}
				}
			}

			const radius = new THREE.Vector3().subVectors(selectionBox.max, selectionBox.min).length() * 0.5;
			distance = radius / Math.abs(Math.sin(THREE.MathUtils.degToRad(60.0) * 0.5));
			if (!distance) distance = defaultFocusDistance;

			if (camera instanceof THREE.OrthographicCamera)
			{
				const boxSize = new THREE.Vector3();
				selectionBox.getSize(boxSize);
				let maxDimension = Math.max(boxSize.x, boxSize.y, boxSize.z);
				if (!maxDimension)
				{
					maxDimension = 1.0;
				}
				else
				{
					maxDimension *= 2.5;
				}
				const sceneViewData = WorkspaceManager.getSceneViewData(Engine.getInstance().getActiveSceneViewId());
				const renderRect = sceneViewData?.context.canvas.getBoundingClientRect() || { width: window.innerWidth, height: window.innerHeight };
				const aspectRatio = Math.ceil(renderRect.width) / Math.ceil(renderRect.height);
				orthographic = {
					left: maxDimension * aspectRatio * -0.5,
					right: maxDimension * aspectRatio * 0.5,
					top: maxDimension * 0.5,
					bottom: maxDimension * -0.5
				}
			}

			selectionBox.getCenter(anchor);
		}

		return {
			anchor: anchor,
			position: new THREE.Vector3().subVectors(anchor, new THREE.Vector3().copy(cameraForward).multiplyScalar(distance)),
			orthographic: orthographic
		}
	}

	focus(objects = Engine.getInstance().getPickingManager().getCurrentSelection().objects)
	{
		const {position, anchor, orthographic} = SceneCameraController.focusOnObjects(this._camera, objects);
		if (this._camera instanceof THREE.OrthographicCamera && orthographic)
		{
			this._camera.left = orthographic.left;
			this._camera.right = orthographic.right;
			this._camera.top = orthographic.top;
			this._camera.bottom = orthographic.bottom;
			this._camera.updateProjectionMatrix();
		}
		this._anchor.copy(anchor);
		this.setTransformAnimation(position);
		Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
	}


	alignToView()
	{
		new AlignToViewAction(Engine.getInstance().getPickingManager().getCurrentSelection().objects, this._camera);
	}

	goHome(animate = true)
	{
		const scene = Engine.getInstance().getCurrentScene();
		const targetPosition = new THREE.Vector3().copy(SceneCameraController.homePosition);
		const targetForward = new THREE.Vector3();
		const quaternion = new THREE.Quaternion();
		const anchorPosition = new THREE.Vector3(0, 0, 0 );
		let interactiveCamera: ScopeCamera | null = null;
		const distance = SceneCameraController.homePosition.distanceTo(new THREE.Vector3(0, 0, 0));
		if (scene)
		{
			interactiveCamera = ScopeCamera.getInteractiveModeCamera(scene);
			if (interactiveCamera)
			{
				targetPosition.copy(interactiveCamera.position);
				interactiveCamera.getWorldDirection(targetForward);
				quaternion.copy(interactiveCamera.quaternion);
				anchorPosition.copy(new THREE.Vector3().copy(targetPosition).add(new THREE.Vector3().copy(targetForward).multiplyScalar(-distance)));
			}
		}
		if (!interactiveCamera)
		{
			quaternion.setFromRotationMatrix(new THREE.Matrix4().lookAt(targetPosition, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 1, 0)));
		}
		if (animate)
		{
			this.setTransformAnimation(targetPosition.clone(), quaternion.clone());
		}
		else
		{
			this._camera.position.copy(targetPosition);
			this._camera.quaternion.copy(quaternion);
		}
		this._anchor.copy(anchorPosition);
		Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
	}

	setTransformAnimation(targetPosition: THREE.Vector3, targetQuaternion?: THREE.Quaternion, keepLookAt = false): boolean
	{
		this._animationTargetPosition = targetPosition;		
		const focusObject = Engine.getInstance().focusObject;
		const updateAnchor = this.isSimulatingAsViewer && focusObject;
		if(updateAnchor)
		{
			const lookAtMatrix = new THREE.Matrix4();
			lookAtMatrix.lookAt(targetPosition, focusObject.position, this._camera.up);
			this.animationTargetQuaternion.setFromRotationMatrix(lookAtMatrix);
			this._anchor.copy(focusObject.position);
		}
		else
		{
			this.animationTargetQuaternion.copy(targetQuaternion ? targetQuaternion : this._camera.quaternion);	
		}
		this._animateTransform = true;
		this.keepLookAtDuringAnimation = keepLookAt;
		this.animationTime = 0;
		return !updateAnchor;
	}

	setProjectionAnimation(currentProjection: THREE.Matrix4, targetProjection: THREE.Matrix4)
	{
		this.animateProjection = true;
		this.animationTime = 0;
		this.currentProjectionMatrix = currentProjection.clone();
		this.targetProjectionMatrix = targetProjection.clone();
	}

	animate()
	{
		if (this.animationTime < this.animationDuration)
		{
			this.animationTime = Math.min(this.animationDuration, this.animationTime + Time.getDeltaTime());
			const t = this.animationTime / this.animationDuration;

			if(this._animateTransform)
			{
				this._camera.position.lerp(this._animationTargetPosition, t);
				if (this.keepLookAtDuringAnimation)
				{
					this._camera.lookAt(this._anchor);
				}
				else
				{
					this._camera.quaternion.slerp(this.animationTargetQuaternion, t);
				}
				if (t >= 1.0)
				{
					this._animateTransform = false;
					this._camera.position.copy(this._animationTargetPosition);
				}
			}

			if(this.animateProjection)
			{
				for(let i = 0; i < 16; i++)
				{
					this._camera.projectionMatrix.elements[i] = MathUtils.lerpValue(this.currentProjectionMatrix.elements[i], this.targetProjectionMatrix.elements[i], t, ValueTypes.Scalar) as number;
				}
				if (t >= 1.0)
				{
					this.animateProjection = false;
					this._camera.projectionMatrix.copy(this.targetProjectionMatrix);
				}
				this._camera.projectionMatrixInverse.copy(this._camera.projectionMatrix).invert();
			}

			Engine.getInstance().getPostProcessManager().setOutlineRenderDirty(true, Engine.getInstance().getActiveSceneViewId());
		}
	}

	getAnchor()
	{
		return this._anchor;
	}
}
